import { cn } from '@/lib/cn';

type ProgressBarProps = {
  value: number;
  max: number;
  label?: string;
  /** Threshold ratios for color changes (default 0.8 warn, 1.0 danger). */
  warnAt?: number;
  dangerAt?: number;
  className?: string;
};

/**
 * Visual quota gauge. Pure CSS — no charting lib needed.
 * Color shifts:
 *   <80%   → accent (amber)
 *   80-99% → warn (orange)
 *   ≥100%  → danger (red, capped visually at 100%)
 */
export function ProgressBar({
  value,
  max,
  label,
  warnAt = 0.8,
  dangerAt = 1.0,
  className,
}: ProgressBarProps) {
  const safeMax = Math.max(1, max);
  const ratio = value / safeMax;
  const widthPct = Math.min(100, Math.max(0, ratio * 100));

  const tone =
    ratio >= dangerAt ? 'danger' : ratio >= warnAt ? 'warn' : 'ok';

  const fillClass =
    tone === 'danger'
      ? 'bg-ducens-danger'
      : tone === 'warn'
        ? 'bg-ducens-warn'
        : 'bg-ducens-accent';

  const captionClass =
    tone === 'danger'
      ? 'text-ducens-danger'
      : tone === 'warn'
        ? 'text-ducens-warn'
        : 'text-ducens-muted';

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-baseline justify-between mb-1.5">
        {label && (
          <span className="text-xs uppercase tracking-wide text-ducens-muted font-medium">
            {label}
          </span>
        )}
        <span className={cn('text-xs tabular-nums font-medium', captionClass)}>
          {value.toLocaleString('fr-FR')} / {max.toLocaleString('fr-FR')}
          {' · '}
          {Math.round(ratio * 100)}%
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label ?? 'progression'}
        className="h-2 w-full bg-ducens-border rounded-full overflow-hidden"
      >
        <div
          className={cn('h-full transition-all duration-500', fillClass)}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      {tone === 'warn' && (
        <p className="text-xs text-ducens-warn mt-1.5">
          Tu approches ta limite mensuelle. Pense à upgrade ou attendre le reset.
        </p>
      )}
      {tone === 'danger' && (
        <p className="text-xs text-ducens-danger mt-1.5">
          Quota atteint. Les agents sont en pause jusqu'au reset mensuel ou upgrade.
        </p>
      )}
    </div>
  );
}
